import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { FormType } from "../../common";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { Maybe } from "../../../../../../graphql/types";
import {
  dairyAshContentValue,
  dairyGrossEnergyContentValue,
} from "../../baselineDefaultValues";

interface DairyV2FeedFreshGrassComponentProps {
  fieldItemPrefix: string;
  formType: FormType;
  itemIndex: number;
  isDairyCowStage: boolean;
  // eslint-disable-next-line react/require-default-props
  removeHandler?: Maybe<(index: number) => void>;
  shouldDisplayResetButton?: Maybe<(index: number) => void>;
  isIntervention?: boolean;
  isInterventionEdit?: boolean;
  changeHandler?: () => void;
}

const DairyV2FeedFreshGrassComponent: FC<DairyV2FeedFreshGrassComponentProps> = ({
  fieldItemPrefix,
  formType,
  itemIndex,
  isDairyCowStage,
  removeHandler,
  shouldDisplayResetButton,
  isIntervention = false,
  isInterventionEdit = false,
  changeHandler = null,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fieldPrefix = `${fieldItemPrefix}.${itemIndex}`;
  const formContext = useFormContext();
  const freshGrassTypeWatch = useWatch({ name: `${fieldPrefix}.type` });
  const adornment = "kg DM";

  const typeFieldChangeActions = () => {
    if (shouldDisplayResetButton) {
      shouldDisplayResetButton(itemIndex);
    }

    formContext.trigger(fieldPrefix);
  };

  const renderRemoveButton = () => {
    if (isIntervention) {
      return isInterventionEdit;
    }

    return (
      formType !== FormType.View &&
      removeHandler && (
        itemIndex !== undefined &&
        itemIndex !== null &&
        itemIndex !== 0
      )
    );
  }

  return (
    <div
      style={{
        border: "1px solid var(--dsm-color-neutral-lighter)",
        padding: "16px 16px 0px 16px",
        marginBottom: "16px",
      }}
    >
      {renderRemoveButton() && (
        <DsmButton
          variant="text"
          style={{ position: "relative", width: "100%" }}
          onClick={() => removeHandler!(itemIndex)}
        >
          <DsmIcon
            name="general/x-close"
            style={{
              position: "absolute",
              color: "var(--dsm-color-neutral-darker",
              right: "0",
            }}
          />
        </DsmButton>
      )}
      <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
        <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
          <ReactHookDsmInput
            type="text"
            name={`${fieldPrefix}.type`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.FEED.FRESH_GRASS_NAME",
            })}
            tooltip={intl.formatMessage({
              id: isDairyCowStage
                ? "SUSTELL.STAGE.DAIRY.FEED.FRESH_GRASS_NAME.TOOLTIP"
                : "SUSTELL.STAGE.DAIRY.FEED.CONTINUOUS_GROWING.FRESH_GRASS_NAME.TOOLTIP",
            })}
            adornment={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.FEED.FRESH_GRASS_NAME.ADORNMENT",
            })}
            disabled={formType === FormType.View || (isIntervention && !isInterventionEdit)}
            defaultValue={
              formContext.getValues(`${fieldPrefix}.type`) as string
            }
            changeHandler={typeFieldChangeActions}
          />
          <ReactHookDsmInput
            type="number"
            name={`${fieldPrefix}.amount`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.FEED.AMOUNT",
            })}
            tooltip={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.FEED.AMOUNT.TOOLTIP",
            })}
            adornment={adornment}
            disabled={formType === FormType.View || (isIntervention && !isInterventionEdit)}
            defaultValue={
              formContext.getValues(`${fieldPrefix}.amount`) as string
            }
            required={!!freshGrassTypeWatch}
            changeHandler={changeHandler}
          />
          {(freshGrassTypeWatch || isIntervention) && (
            <ReactHookDsmInput
              type="number"
              name={`${fieldPrefix}.digestibility`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.DIGESTIBILITY.PLACEHOLDER",
              })}
              disabled={formType === FormType.View || (isIntervention && !isInterventionEdit)}
              defaultValue={
                formContext.getValues(`${fieldPrefix}.digestibility`) as string
              }
              required
            />
          )}
          {(freshGrassTypeWatch || isIntervention) && isDairyCowStage && (
            <ReactHookDsmInput
              type="number"
              name={`${fieldPrefix}.neutralDetergentFiber`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.NEUTRAL_DETERGENT_FIBER.PLACEHOLDER",
              })}
              disabled={formType === FormType.View || (isIntervention && !isInterventionEdit)}
              defaultValue={
                formContext.getValues(
                  `${fieldPrefix}.neutralDetergentFiber`
                ) as string
              }
              required
            />
          )}
          {(freshGrassTypeWatch || isIntervention) && !isDairyCowStage && (
            <ReactHookDsmInput
              type="number"
              name={`${fieldPrefix}.grossEnergyContent`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.PLACEHOLDER",
              })}
              disabled={formType === FormType.View || (isIntervention && !isInterventionEdit)}
              defaultValue={
                (formContext.getValues(
                  `${fieldPrefix}.grossEnergyContent`
                ) as string) || String(dairyGrossEnergyContentValue)
              }
            />
          )}
        </DsmGrid>
        <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
          {(freshGrassTypeWatch || isIntervention) && (
            <ReactHookDsmInput
              type="number"
              name={`${fieldPrefix}.crudeProteinContent`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.CRUDE_PROTEIN_CONTENT.PLACEHOLDER",
              })}
              disabled={formType === FormType.View || (isIntervention && !isInterventionEdit)}
              defaultValue={
                formContext.getValues(
                  `${fieldPrefix}.crudeProteinContent`
                ) as string
              }
              required
            />
          )}
          {(freshGrassTypeWatch || isIntervention) && (
            <ReactHookDsmInput
              type="number"
              name={`${fieldPrefix}.dryMatterContent`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.DRY_MATTER_CONTENT.PLACEHOLDER",
              })}
              disabled={formType === FormType.View || (isIntervention && !isInterventionEdit)}
              defaultValue={
                formContext.getValues(
                  `${fieldPrefix}.dryMatterContent`
                ) as string
              }
              required
            />
          )}
          {(freshGrassTypeWatch || isIntervention) && isDairyCowStage && (
            <ReactHookDsmInput
              type="number"
              name={`${fieldPrefix}.grossEnergyContent`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.GROSS_ENERGY_CONTENT.PLACEHOLDER",
              })}
              disabled={formType === FormType.View || (isIntervention && !isInterventionEdit)}
              defaultValue={
                (formContext.getValues(
                  `${fieldPrefix}.grossEnergyContent`
                ) as string) || String(dairyGrossEnergyContentValue)
              }
            />
          )}
          {(freshGrassTypeWatch || isIntervention) && (
            <ReactHookDsmInput
              type="number"
              name={`${fieldPrefix}.ashContent`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT",
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.ADORNMENT",
              })}
              placeholder={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.ASH_CONTENT.PLACEHOLDER",
              })}
              disabled={formType === FormType.View || (isIntervention && !isInterventionEdit)}
              defaultValue={
                (formContext.getValues(
                  `${fieldPrefix}.ashContent`
                ) as string) || String(dairyAshContentValue)
              }
            />
          )}
        </DsmGrid>
      </DsmGrid>
    </div>
  );
};

export default DairyV2FeedFreshGrassComponent;
